// FramePay's `ready` waits on a config fetch to api-order.payments.ai, then on a
// handshake with a hidden controller iframe that only starts once the iframe fires
// `load`. Either can stall without an `error` event, leaving `Rebilly.initialized`
// true. These fields show which stage a stalled init reached.
const FRAMEPAY_HOST = /(^|\.)(payments\.ai|rebilly\.com|kaptcha\.com)$/
const MAX_RESOURCES = 15
// Loading rebilly.js warms the cache with hidden `<page>#prefetch` iframes, including the
// controller page, before initialize() runs. Those say nothing about the real controller.
const PREFETCH = '#prefetch'

type ResourceEntry = PerformanceResourceTiming & { responseStatus?: number }

export type PAIInitDiagnostics = {
  recordRebillyError: (code: unknown) => void
  snapshot: () => Record<string, unknown>
  stop: () => void
}

export const startPAIInitDiagnostics = (): PAIInitDiagnostics => {
  const startedAt = performance.now()
  const rebillyAtStart = globalThis.Rebilly
  const startedHidden = document.visibilityState === 'hidden'
  let hiddenSince: number | null = startedHidden ? startedAt : null
  let hiddenMs = 0
  const rebillyErrorCodes: string[] = []

  // The default 250-entry buffer can fill up on a busy page before FramePay loads.
  performance.setResourceTimingBufferSize?.(1000)

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince ??= performance.now()
    } else if (hiddenSince !== null) {
      hiddenMs += performance.now() - hiddenSince
      hiddenSince = null
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  const framepayResources = () =>
    (performance.getEntriesByType('resource') as ResourceEntry[])
      .map((entry) => ({ entry, url: new URL(entry.name, location.href) }))
      .filter(({ url }) => FRAMEPAY_HOST.test(url.hostname))

  const collect = (): Record<string, unknown> => {
    const now = performance.now()
    const resources = framepayResources()
    return {
      startedHidden,
      hiddenMs: Math.round(hiddenMs + (hiddenSince !== null ? now - hiddenSince : 0)),
      rebillyErrorCodes,
      configFetched: !!globalThis.Rebilly?.controllerFrame,
      controllerIframesInDom: document.querySelectorAll(`iframe[src*="/controller/"]:not([src$="${PREFETCH}"])`).length,
      controllerIframeLoaded: resources.some(({ url }) => url.pathname.includes('/controller/') && url.hash !== PREFETCH),
      framepayScriptTags: document.querySelectorAll('script[src*="framepay"]').length,
      sameRebillyInstance: globalThis.Rebilly === rebillyAtStart,
      online: navigator.onLine,
      effectiveConnectionType: (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
        ?.effectiveType,
      framepayResources: resources.slice(-MAX_RESOURCES).map(({ entry, url }) => ({
        url: `${url.hostname}${url.pathname}${url.hash === PREFETCH ? PREFETCH : ''}`.slice(0, 110),
        type: entry.initiatorType,
        startMs: Math.round(entry.startTime - startedAt),
        durationMs: Math.round(entry.duration),
        status: entry.responseStatus,
      })),
    }
  }

  return {
    recordRebillyError: (code) => {
      rebillyErrorCodes.push(String(code))
    },
    // Runs inside the checkout's init path, so it must never throw.
    snapshot: () => {
      try {
        return collect()
      } catch (e) {
        return { diagnosticsError: String(e) }
      }
    },
    stop: () => document.removeEventListener('visibilitychange', onVisibilityChange),
  }
}
